// src/api/useAssetApi.ts
import { useMemo } from "react"; // 1. Import useMemo
import apiClient from "./apiClient";
import { Asset, User } from "../state/types";
import axios, { AxiosProgressEvent } from "axios";

// --- PRIVATE HELPERS (Keep outside - these are already stable) ---
const isYyyyMmDd = (v: unknown): v is string =>
  typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);

const normalizeAssetDateFilters = (body: Record<string, any>) => {
  const next = { ...body };
  if (isYyyyMmDd(next.start_date)) {
    const d = new Date(`${next.start_date}T00:00:00`);
    next.start_date = String(Math.floor(d.getTime() / 1000));
  }
  if (isYyyyMmDd(next.end_date)) {
    const d = new Date(`${next.end_date}T00:00:00`);
    d.setDate(d.getDate() + 1);
    next.end_date = String(Math.floor(d.getTime() / 1000) - 1);
  }
  return next;
};

const mapAssetResponse = (asset: any): Asset => {
  const rawSystemCode = asset.systemCode || asset.clientIdentifier || "";
  const cleanedSystemCode = /U\d{2}$/.test(rawSystemCode) ? rawSystemCode.slice(0, -3) : rawSystemCode;

  return {
    ...asset,
    systemCode: cleanedSystemCode,
    created_at: asset.sk,
    config: asset.configurationName,
  };
};

// --- THE HOOK ---
export const useAssetApi = () => {
  // 2. Wrap everything in useMemo
  return useMemo(() => {
    return {
      getAssetErrors: async (client_id: string, asset_id: string) => {
        const response = await apiClient.post('/asset/errors', { client_id, asset_id });
        return response.data;
      },

      getAssetsApi: async (body: Record<string, any>): Promise<{
        assets: Asset[];
        total: number;
        aliasResolution?: { systemCode: string; imageSet: string | null } | null;
      }> => {
        try {
          const normalizedBody = normalizeAssetDateFilters(body);
          if (normalizedBody.system_code === "undefined") {
            return { assets: [], total: 0 };
          }
          const { data } = await apiClient.post<{
            assets: Asset[];
            total_assets: number;
            aliasResolution?: { systemCode: string; imageSet: string | null } | null;
          }>('/assets', normalizedBody);
          return {
            assets: data.assets.map(mapAssetResponse),
            total: data.total_assets,
            aliasResolution: data.aliasResolution ?? null,
          };
        } catch (error) {
          console.error('Error fetching assets:', error);
          return { assets: [], total: 0 };
        }
      },

      createAssetApi: async (payload: {
        assetIdentifier: string;
        systemCode: string | undefined;
        rooftopName: string;
        files: Array<{ filename: string; contentType: string; imageType: string }>;
      }) => {
        const { data } = await apiClient.post<{ pk: string; presignedUrls: Record<string, string> }>(
          '/asset/create', 
          payload
        );
        return data;
      },

      finalizeAssetApi: async (payload: {
        pk: string;
        imageExterior: Array<{ filename: string; imageTag: string }>;
        imageManual: Array<{ filename: string; imageTag: string }>;
        imagePano: Array<{ filename: string; imageTag: string }>;
        imageUndercarriage: Array<{ filename: string; imageTag: string }>;
      }) => {
        const { data } = await apiClient.post('/asset/finalize', payload);
        return data;
      },

      reprocessAssetApi: async (payload: { pk: string; sk: string; target: string }) => {
        const { data } = await apiClient.post('/asset/reprocess', payload);
        return data;
      },

      secondaryProcessAssetApi: async (payload: { pk: string }) => {
        const { data } = await apiClient.post('/asset/secondaryProcess', payload);
        return data;
      },

      removeAssetRedriveApi: async (payload: {
        pk: string;
        redriveType: 'crs' | 'mashery';
        sk?: string;
      }) => {
        const { data } = await apiClient.delete('/asset/removeRedrive', { data: payload });
        return data;
      },

      runAssetApi: async (payload: {
        assetId: string;
        rooftop_id: string;
        system_code: string;
      }) => {
        const { data } = await apiClient.post('/run-asset', {
          assetId: payload.assetId,
          rooftop_id: payload.rooftop_id,
          system_code: payload.system_code,
        });
        return data;
      },

      updateAssetIdApi: async (payload: {
        pk: string;
        oldAssetId: string;
        newAssetId: string;
        user: User | null;
        reason: string;
        updateTime: string;
      }) => {
        const { data } = await apiClient.post('/asset/rename', payload);
        return data;
      },

      getUploadUrlApi: async (payload: {
        clientName: string;
        pk: string;
        fileName: string;
        target: string;
        contentType: string;
      }) => {
        const { data } = await apiClient.put('/asset/upload', payload);
        return data.presignedUrl as string;
      },

      uploadToS3Api: async (
        url: string, 
        file: File, 
        onProgress?: (percent: number) => void
      ) => {
        return axios.put(url, file, {
          headers: { 'Content-Type': file.type || 'image/jpeg' },
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (onProgress) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / (progressEvent.total || 1)
              );
              onProgress(percentCompleted);
            }
          },
        });
      },

      updateImageOrderApi: async (payload: {
        pk: string;
        sk: string;
        imageType: string;
        imageArray: any[];
      }) => {
        const { data } = await apiClient.put('/asset/updateImageOrder', payload);
        return data;
      },

      updateImageTagApi: async (payload: {
        pk: string;
        sk: string;
        imageType: string;
        imageIndex: number;
        newTag: string;
      }) => {
        const { data } = await apiClient.put('/asset/updateImageTag', payload);
        return data;
      },

      deleteImageApi: async (payload: {
        pk: string;
        sk: string;
        imageType: string;
        imageIndex: number;
      }) => {
        const { data } = await apiClient.delete('/asset/deleteImage', { data: payload });
        return data;
      },
    };
  }, []); // 3. Dependency array is empty because these functions only depend on external imports
};
